import { apiClient, signApiRequest } from ".";
import { useQuery } from "@tanstack/react-query";
import { CustomerCredentials } from "../ui/customerDataStore";
import { captureException } from "@sentry/react";

export const getSignStatus = async ({
  id,
  credentials,
}: {
  id: string;
  credentials: CustomerCredentials;
}) => {
  const signedRequest = await signApiRequest(
    "GET",
    `${import.meta.env.VITE_API_URL}/auth/auth?id=${id}`,
    credentials,
    { "content-type": "application/json" },
  );

  const response = await apiClient(signedRequest);
  return response.data;
};

export const useGetSignStatus = ({
  id,
  enabled,
  credentials,
}: {
  id: string;
  enabled: boolean;
  credentials: CustomerCredentials;
}) => {
  const signStatusQuery = useQuery({
    queryKey: ["get-sign-status", id],
    queryFn: () => getSignStatus({ id, credentials }),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    refetchInterval: (query) => {
      const status = query.state.data?.body?.status;
      if (status === "complete" || status === "failed") {
        return false;
      }
      return 500;
    },
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    enabled: enabled,
    throwOnError: (error) => {
      captureException(error, {
        tags: {
          operation: "get-sign-status",
          errorType: error.name || "UnknownError",
        },
        extra: {
          apiEndpoint: `/auth/auth?id=${id}`,
          transactionId: id,
          errorMessage: error.message,
        },
        level: "error",
      });
      return false;
    },
  });
  return signStatusQuery;
};
